import React from "react";
import "./DownloadDropdown.scss";
import { DownloadIcon } from "../Icons/DownloadIcon";
import { CheckSuccess } from "../Icons/CheckSuccess";
import { ErrorIcon } from "../Icons/ErrorIcon";
import { getTimeAgo } from "../Utils";

const transformDownloadData = (rawData) => {
  const inProgress = [];
  const ready = [];
  const failed = [];

  Object.entries(rawData).forEach(([_, entry]) => {
    const title = entry.folderName;
    const created = entry.created;
    const time = created ? getTimeAgo(created) : "";

    if (entry.status === "InProgress") {
      inProgress.push({
        title,
        time,
        downloaded: entry.contactFetchedProfiles || entry.currentCountOfProfiles,
        total: entry.totalProfiles,
        created,
      });
    } else if (["Completed", "PartiallyDownload"].includes(entry.status)) {
      ready.push({
        title,
        time,
        downloaded:entry.contactFetchedProfiles || entry.currentCountOfProfiles,
        total: entry.totalProfiles,
        isComplete: entry.status === "Completed",
        downloadUrl: entry.emailLink,
        created,
      });
    } else {
      failed.push({
        title,
        time,
        error:
          entry.status === "NoService"
            ? "Download failed. The database service is not enabled for your login."
            : entry.status === "InsufficientInventory"
            ? "Download failed due to insufficient inventory."
            : "Download failed. Please revisit the folder to try again.",
        created,
      });
    }
  });
  const sortByCreatedDesc = (a, b) => new Date(b.created) - new Date(a.created);

  inProgress.sort(sortByCreatedDesc);
  ready.sort(sortByCreatedDesc);
  failed.sort(sortByCreatedDesc);

  return { inProgress, ready, failed };
};
const triggerDownload = (url) => {
  const a = document.createElement("a");
  a.href = url;
  a.download = "";
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};
const DownloadItem = ({
  icon,
  iconClass,
  title,
  time,
  detail,
  note,
  actionUrl,
  actionText,
  error,
}) => (
  <div className="download-dropdown__item-row">
    <div className={`download-dropdown__icon ${iconClass}`}>{icon}</div>
    <div className="download-dropdown__item-content">
      <div className="download-dropdown__item-header">
        <span className="download-dropdown__item-title">{title}</span>
        <span className="download-dropdown__time">{time}</span>
      </div>
      {detail && <p className="download-dropdown__item-detail">{detail}</p>}
      {note && <p className="download-dropdown__note">{note}</p>}
      {actionUrl && (
        <>
          <hr className="download-dropdown__divider" />
          <p
            className="download-dropdown__action"
            onClick={() => triggerDownload(actionUrl)}
          >
            {actionText}
          </p>
        </>
      )}
      {error && <p className="download-dropdown__error">{error}</p>}
    </div>
  </div>
);

const DownloadDropdown = ({ data, open }) => {
  const { inProgress, ready, failed } = transformDownloadData(data);

  return (
    <div className={`download-dropdown ${open ? "show" : "hide"}`}>
      <div className="download-dropdown__title">Downloads</div>
      <div
        style={{
          maxHeight: "430px",
          overflowY: "auto",
          paddingRight: "12px",
          display: "flex",
          flexDirection: "column",
          gap: "16px",
        }}
      >
        {inProgress.length > 0 && (
          <div className="download-dropdown__section">
            <div className="download-dropdown__section-title">
              In-progress ({inProgress.length})
            </div>
            {inProgress.map((item, i) => (
              <div key={i} className="download-dropdown__item is-in-progress">
                <DownloadItem
                  icon={<DownloadIcon />}
                  iconClass="download-dropdown__icon--loader"
                  title={item.title}
                  time={item.time}
                  detail={
                    <span className="download-dropdown__status-text">
                      Fetching The Details (
                      {((item.downloaded * 100) / item.total).toFixed(2)}%)
                    </span>
                  }
                />
              </div>
            ))}
          </div>
        )}

        {ready.length > 0 && (
          <div className="download-dropdown__section">
            <div className="download-dropdown__section-title">
            Completed ({ready.length})
            </div>
            {ready.map((item, i) => (
              <div key={i} className="download-dropdown__item is-ready">
                <DownloadItem
                  icon={<CheckSuccess />}
                  iconClass="download-dropdown__icon--check"
                  title={item.title}
                  time={item.time}
                  detail={
                    <span className="download-dropdown__item-detail">
                      <span className="download-dropdown__consumed-count">
                        {item.downloaded}
                      </span>
                      <span className="download-dropdown__total-count">
                        /{item.total}
                      </span>{" "}
                      -{" "}
                      <span className="download-dropdown__status-text">
                        {item.isComplete
                          ? "Fetching details completed"
                          : "Partial data is ready"}
                      </span>
                    </span>
                  }
                  note={
                    !item.isComplete
                      ? "We’re collecting the remaining  contact info—check your inbox in a bit!"
                      : undefined
                  }
                  actionUrl={item.downloadUrl}
                  actionText={
                    item.isComplete ? "Download again" : "Download partially"
                  }
                />
              </div>
            ))}
          </div>
        )}

        {failed.length > 0 && (
          <div className="download-dropdown__section">
            <div className="download-dropdown__section-title">
              Failed ({failed.length})
            </div>
            {failed.map((item, i) => (
              <div key={i} className="download-dropdown__item is-failed">
                <DownloadItem
                  icon={<ErrorIcon />}
                  iconClass="download-dropdown__icon--alert"
                  title={item.title}
                  time={item.time}
                  error={item.error}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DownloadDropdown;
